// Reads the current user's daily usage counters from `daily_usage` so the
// UI can render "X / Y used today" gauges and disable buttons proactively.
// The server still enforces — this is a UX hint only.
//
// Counts reset at UTC midnight (matches the table's `day_utc` column).
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type UsageKind = "chat" | "image" | "research" | "guide";

export type DailyUsageMap = Record<UsageKind, number>;

const EMPTY: DailyUsageMap = { chat: 0, image: 0, research: 0, guide: 0 };

export const useDailyUsage = () => {
  const { user } = useAuth();
  const [usage, setUsage] = useState<DailyUsageMap>(EMPTY);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setUsage(EMPTY);
      return;
    }
    setLoading(true);
    // UTC date string matches the `day_utc` column default. Doing the
    // conversion client-side keeps the query simple and avoids an RPC.
    const todayUtc = new Date().toISOString().slice(0, 10);
    const { data, error } = await supabase
      .from("daily_usage")
      .select("kind, count")
      .eq("user_id", user.id)
      .eq("day_utc", todayUtc);
    setLoading(false);
    if (error) {
      console.error("daily_usage fetch failed", error);
      return;
    }
    const next: DailyUsageMap = { ...EMPTY };
    for (const row of data ?? []) {
      const k = row.kind as UsageKind;
      if (k in next) next[k] = row.count ?? 0;
    }
    setUsage(next);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { usage, loading, refresh };
};
